import { Stack, TextField, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useAssetById } from "../data/Assets/useAssetById";
import {
  getAssetTypeLabel,
  managedSurveyAssetType,
  UTP2_VARIABLE_CHAR,
} from "../shared/constants/managedSurveyAssetTypes";
import { RAGStatus } from "../shared/enums/Result";
import { ResultSurveyableAsset } from "../shared/interfaces/Result";
import { SurveyableAsset } from "../shared/interfaces/SurveyableAsset";

interface SwitchgearLabelProps {
  switchgear: SurveyableAsset | ResultSurveyableAsset;
  editing: boolean;
  onChange?: (updated: SurveyableAsset | ResultSurveyableAsset) => void;
  isSelected?: boolean;
  status?: RAGStatus;
  partialResultSelectedAssetName?: string;
}

function formatAssetType(assetType?: string): string {
  if (!assetType) return managedSurveyAssetType.Switchgear;
  return assetType.startsWith(UTP2_VARIABLE_CHAR) ? assetType : getAssetTypeLabel(assetType);
}

export function SwitchgearLabel(props: SwitchgearLabelProps) {
  const { switchgear, editing, onChange, status, isSelected } = props;
  const theme = useTheme();
  const { t } = useTranslation();

  const ID_KEY = "id";
  const ASSET_ID_KEY = "AssetId";
  const ASSET_NAME_KEY = "AssetName";

  const assetId =
    ID_KEY in switchgear
      ? switchgear.id
      : ASSET_ID_KEY in switchgear
        ? (switchgear as ResultSurveyableAsset).AssetId
        : 0;

  const asset = useAssetById(assetId).data;

  const isResultLabel = ASSET_NAME_KEY in switchgear;

  const switchgearName = props.partialResultSelectedAssetName
    ? props.partialResultSelectedAssetName
    : isResultLabel
      ? (asset?.name ?? switchgear.AssetName)
      : switchgear.name;

  const statusColors = {
    [RAGStatus.Red]: theme.palette.redStatus.main,
    [RAGStatus.Amber]: theme.palette.amberStatus.main,
    [RAGStatus.Green]: theme.palette.greenStatus.main,
    [RAGStatus.None]: theme.palette.tabs.contrastText,
  };

  const statusColor = status ? statusColors[status] : theme.palette.tabs.contrastText;

  const formattedAssetType = formatAssetType(isResultLabel ? switchgear.AssetType : switchgear.assetType);

  if (!editing)
    return (
      <Stack sx={{ paddingBottom: 1 }} alignItems="center">
        <Typography style={{ textTransform: "uppercase" }}>{switchgearName}</Typography>
        <Stack direction={"row"} alignItems="center">
          {"AssetName" in switchgear && (
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusColor,
                marginRight: 8,
              }}
            />
          )}
          <Typography
            variant="body2"
            color={
              isResultLabel ? statusColor : isSelected ? theme.palette.tabs.light : theme.palette.tabs.contrastText
            }
          >
            {t(`assetTypes.${formattedAssetType}`)}
          </Typography>
        </Stack>
      </Stack>
    );

  return (
    <Stack sx={{ paddingBottom: 1 }}>
      <TextField
        inputProps={{ min: 0, style: { textAlign: "center" } }}
        variant="standard"
        value={switchgearName}
        onChange={(event) => {
          const updatedSwitchgear = isResultLabel
            ? { ...switchgear, AssetName: event.target.value }
            : { ...switchgear, name: event.target.value };
          onChange?.(updatedSwitchgear);
        }}
        sx={{
          textTransform: "uppercase",
        }}
      />
      <Typography variant="body2" color={theme.palette.tabs.contrastText}>
        {t(`assetTypes.${formattedAssetType}`)}
      </Typography>
    </Stack>
  );
}
